/**
 * WorkPerformedSection Component
 * Dynamic work activity entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, HardHat, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { WorkEntry } from '../../../types/dailyLog';
import { Input, NumberInput, TextArea } from '../../forms';

// Common work activities
const ACTIVITY_OPTIONS = [
  'Site Preparation',
  'Excavation',
  'Foundation Work',
  'Concrete Pouring',
  'Concrete Finishing',
  'Structural Steel',
  'Framing',
  'Roofing',
  'Waterproofing',
  'Insulation',
  'Drywall Installation',
  'Drywall Finishing',
  'Electrical Rough-In',
  'Electrical Finish',
  'Plumbing Rough-In',
  'Plumbing Finish',
  'HVAC Installation',
  'Painting',
  'Flooring',
  'Tile Work',
  'Cabinet Installation',
  'Trim Work',
  'Landscaping',
  'Paving',
  'Cleanup',
  'Inspection Preparation',
  'Punch List',
  'Other',
];

interface WorkPerformedSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'workPerformed', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const WorkPerformedSection: React.FC<WorkPerformedSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const workErrors = errors.workPerformed as unknown as FieldErrors<WorkEntry>[] | undefined;

  const handleAddEntry = () => {
    append({
      location: '',
      activity: '',
      trade: '',
      percentComplete: 0,
      notes: '',
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <HardHat className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Work Performed</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'activity' : 'activities'})
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4"
            >
              {/* Entry Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Activity #{index + 1}</span>
                {!disabled && fields.length > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title="Remove entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Location & Activity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name={`workPerformed.${index}.location`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Location"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={workErrors?.[index]?.location?.message}
                      disabled={disabled}
                      required
                      placeholder="Building, floor, room, zone..."
                    />
                  )}
                />

                <Controller
                  name={`workPerformed.${index}.activity`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <Input
                        label="Activity"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={workErrors?.[index]?.activity?.message}
                        disabled={disabled}
                        required
                        placeholder="Select or enter activity"
                        list={`activity-options-${index}`}
                      />
                      <datalist id={`activity-options-${index}`}>
                        {ACTIVITY_OPTIONS.map(activity => (
                          <option key={activity} value={activity} />
                        ))}
                      </datalist>
                    </div>
                  )}
                />
              </div>

              {/* Trade & Percent Complete */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  name={`workPerformed.${index}.trade`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Trade"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={workErrors?.[index]?.trade?.message}
                      disabled={disabled}
                      placeholder="Trade/crew responsible"
                    />
                  )}
                />

                <Controller
                  name={`workPerformed.${index}.percentComplete`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <NumberInput
                        label="Percent Complete"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={workErrors?.[index]?.percentComplete?.message}
                        disabled={disabled}
                        min={0}
                        max={100}
                        step={5}
                      />
                      {/* Progress bar visualization */}
                      {inputField.value > 0 && (
                        <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-600 transition-all duration-300"
                            style={{ width: `${inputField.value}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                />

                <div className="md:col-span-1" />
              </div>

              {/* Notes */}
              <Controller
                name={`workPerformed.${index}.notes`}
                control={control}
                render={({ field: inputField }) => (
                  <TextArea
                    label="Notes"
                    value={inputField.value || ''}
                    onChange={inputField.onChange}
                    error={workErrors?.[index]?.notes?.message}
                    disabled={disabled}
                    rows={2}
                    placeholder="Description of work, progress details..."
                  />
                )}
              />
            </div>
          ))}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Work Activity</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">No work activities recorded</div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkPerformedSection;
